/**
 * CalendarMonth — faithful port of the canonical CalendarMonth screen.
 * 5×7 month grid with day cells, event chips, +N more.
 */
import { useState, useEffect } from "react";
import { SegmentedControl, alpha, Btn, Icon, IconBtn, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { useIsMobile } from "../../shell/useResponsive";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "../events/eventSectionCounts";
import { loadCalendarRange } from "./cal-range";
import { useT } from "../../lib/i18n";
import { civilKey, civilNoon, currentCalendarDate, friendlyCalendarMonth, friendlyCalendarWeekday, toParts } from "../../lib/datetime";

const WEEKDAYS = Array.from({ length: 7 }, (_, day) => new Date(2021, 7, day + 1)); // ui-config: starts on Sunday

/* Fictional event seed for the month view */

export default function CalendarMonth() {
  const t = useT();
  const phone = useIsMobile(640); // the toolbar stays one height on desktop; a phone keeps the 34px segment tap targets
  const [calEvents, setCalEvents] = useState<{title:string;day:number;month:number;color:string}[] | null>(null);
  const nav = useNav();
  const [monthOffset, setMonthOffset] = useState(0);

  // B2 (2026-07-22 docket): "today" follows the tenant display tz, not the
  // browser's local zone — keeps the highlighted cell in step with the tz-aware
  // dashboard near a day boundary.
  const today = currentCalendarDate();
  let m = today.mo + monthOffset;
  let y = today.y;
  while (m > 11) { m -= 12; y++; }
  while (m < 0)  { m += 12; y--; }

  useEffect(() => {
    const first = new Date(y, m, 1);
    const last = new Date(y, m+1, 0);
    // UIR-24 (Voyage #249): typed calendar range op (same source as Week/Day).
    // The month's first and last tenant civil dates as plain y-m-d, both inclusive (CC 0076).
    loadCalendarRange(civilKey(first), civilKey(last))
      .then(({ events }) => {
        // Placed on the TENANT civil date of its start, never the host's getDate().
        setCalEvents(events.map((e) => { const p = toParts(e.startsAt); const d = new Date(e.startsAt); return { title: e.title, day: p ? p.d : d.getDate(), month: p ? p.mo : d.getMonth(), color: e.color }; }));
      }).catch((err) => { console.warn("getCalendar failed:", err); setCalEvents([]); });
  }, [y, m]);

  const first = new Date(y, m, 1);
  const last  = new Date(y, m+1, 0);
  const daysInMonth = last.getDate();
  const firstDow = first.getDay();
  const prevLast = new Date(y, m, 0).getDate();

  const cells: { d:number; out:boolean }[] = [];
  for (let i=firstDow-1; i>=0; i--) cells.push({ d:prevLast-i, out:true });
  for (let d=1; d<=daysInMonth; d++) cells.push({ d, out:false });
  while (cells.length%7!==0) cells.push({ d:cells.length-daysInMonth-firstDow+2, out:true });

  // Build day → events map
  const dayEvents: Record<number, { title:string; color:string }[]> = {};
  let monthCount = 0;
  (calEvents ?? []).forEach((e) => {
    if (e.month !== m) return; // only show when month matches
    (dayEvents[e.day] = dayEvents[e.day] || []).push({ title:e.title, color:e.color });
    monthCount++;
  });

  const isToday = (d: number, out: boolean) =>
    !out && d===today.d && m===today.mo && y===today.y;


  return (
    <Screen rail="calendar" tabs={eventsSectionTabs(nav,"calendar",useEventSectionCounts())} activeTab="events">
      {/* Toolbar */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 24px", background:T.white, borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Btn variant="secondary" size="md" onClick={() => nav.go("events")} icon={<Icon.chev dir="left" size={12}/>}>{t("calendar.month.events", "Events")}</Btn>
          <Btn variant="secondary" size="md" onClick={() => setMonthOffset(0)}>{t("calendar.month.today", "Today")}</Btn>
          <IconBtn label={t("calendar.month.previousMonth", "Previous month")} onClick={() => setMonthOffset(monthOffset-1)}><Icon.chev dir="left"/></IconBtn>
          <IconBtn label={t("calendar.month.nextMonth", "Next month")}     onClick={() => setMonthOffset(monthOffset+1)}><Icon.chev dir="right"/></IconBtn>
          <span style={{ fontSize:16, fontWeight:600, color:T.n1 }}>{friendlyCalendarMonth(civilNoon(first))}</span>
          <Pill tone="primary">{monthCount} {monthCount===1?t("calendar.month.eventb8e1f80b", "event"):t("calendar.month.events862417b9", "events")}</Pill>
          {monthOffset!==0 ? <span onClick={() => setMonthOffset(0)} style={{ fontSize:12, fontWeight:500, color:T.p1, cursor:"pointer" }}>{t("calendar.month.jumpToCurrentMonth", "Jump to current month")}</span> : null}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <IconBtn label={t("calendar.month.filter", "Filter")} onClick={() => toast({ tone:"info", title:t("calendar.month.filterByHostLocationOrType", "Filter by host, location, or type") })}><Icon.filter/></IconBtn>
          <SegmentedControl<"day" | "week" | "month">
            size={phone ? "md" : "sm"}
            ariaLabel={t("calendar.month.ariaLabel", "Calendar view")}
            value="month"
            onChange={(v) => { if (v === "day") nav.go("calendar.day", { day: today.d }); if (v === "week") nav.go("calendar.week");  }}
            /* The three calendar views are configuration, not data. */
            options={[
              { value: "day", label: t("calendar.month.day", "Day") },
              { value: "week", label: t("calendar.month.week", "Week") },
              { value: "month", label: t("calendar.month.month", "Month") },
            ]}
          />
        </div>
      </div>

      {/* Month grid */}
      <div style={{ flex:1, overflow:"auto", padding:16, background:T.canvas }}>
        <div style={{ background:T.white, borderRadius:8, border:`1px solid ${T.border}`, overflow:"hidden" }}>
          {/* Day-of-week headers */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)" }}>
            {WEEKDAYS.map((d,i) => (
              <div key={d.getTime()} style={{ padding:"10px 12px", borderBottom:`1px solid ${T.border}`, borderLeft:i?`1px solid ${T.border}`:"none", fontSize:11, fontWeight:600, color:T.soft, textTransform:"uppercase", letterSpacing:0.04 }}>{friendlyCalendarWeekday(civilNoon(d), "long")}</div>
            ))}
          </div>

          {/* Cells */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gridAutoRows:"minmax(120px, 1fr)" }}>
            {cells.map((cell,i) => {
              const evs = !cell.out ? (dayEvents[cell.d] || []) : [];
              const today = isToday(cell.d, cell.out);
              return (
                <div key={i}
                  onClick={() => !cell.out && nav.go("calendar.day",{ day:cell.d, month:m, year:y })}
                  style={{ padding:8, borderTop:`1px solid ${T.border}`, borderLeft:i%7?`1px solid ${T.border}`:"none", background:cell.out?T.n6:T.white, display:"flex", flexDirection:"column", gap:4, cursor:cell.out?"default":"pointer" }}
                  onMouseEnter={(e) => { if (!cell.out && !today) e.currentTarget.style.background="#FAFAFD"; }}
                  onMouseLeave={(e) => { if (!cell.out && !today) e.currentTarget.style.background=T.white; }}>
                  <div style={{ fontSize:13, fontWeight:today?700:500, color:today?T.white:cell.out?T.n3:T.n1, background:today?T.p1:"transparent", width:24, height:24, borderRadius:999, display:"inline-flex", alignItems:"center", justifyContent:"center", alignSelf:"flex-start" }}>
                    {cell.d}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                    {evs.slice(0,3).map((e,j) => (
                      <div key={j} onClick={(ev)=>{ ev.stopPropagation(); toast({ tone:"info", title:e.title }); }} style={{ background:T.white, borderLeft:`3px solid ${e.color}`, border:`1px solid ${T.border}`, borderLeftColor:e.color, padding:"2px 6px", fontSize:11, color:T.n1, fontWeight:500, borderRadius:3, overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis", cursor:"pointer" }}>
                        {e.title}
                      </div>
                    ))}
                    {evs.length > 3 ? <div style={{ fontSize:11, color:T.p1, fontWeight:600, padding:"0 6px" }}>+ {evs.length-3} {t("calendar.month.more", "more")}</div> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Screen>
  );
}
